/** An end adornment that offers a text field's current value its quick
 *  actions (open the URL, search a grid for the id, copy) without touching
 *  the input itself — the field keeps its primary click and typing.
 *
 *      <TextField {...field} InputProps={{
 *        endAdornment: <FieldActionsAdornment actions={customIdActions(field.value, ctx)} />,
 *      }} />
 *
 *  Pass `actions` computed from the LIVE field value (the Controller's
 *  `field.value`): an empty value yields no actions and the adornment renders
 *  nothing, so a blank field never grows a dead trigger. */
import LinkIcon from '@mui/icons-material/Link';
import { InputAdornment } from '@mui/material';

import { ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';

export function FieldActionsAdornment({
  actions,
  label = 'Value actions',
}: {
  actions: ValueAction[];
  /** The trigger's accessible name (the icon has no text). */
  label?: string;
}) {
  if (actions.length === 0) return null;
  return (
    <InputAdornment position="end">
      <ValueActions chip actions={actions} sx={{ color: 'text.secondary', lineHeight: 0 }}>
        <LinkIcon fontSize="small" titleAccess={label} />
      </ValueActions>
    </InputAdornment>
  );
}

/** `true` for the http(s) URLs the external-open action can take. Deliberately
 *  NOT a type predicate: narrowing an already-`string` value by `value is
 *  string` leaves the negative branch as `never`, which breaks every "else
 *  copy it" fallback. For a user-typed (possibly scheme-less) address, reach
 *  for `looksLikeUrl` / `externalUrl` in `@/utils/externalUrl` instead. */
export function isHttpUrl(value: string | null | undefined): boolean {
  return typeof value === 'string' && /^https?:\/\/\S+$/i.test(value.trim());
}
